import { useEffect, useState, useMemo, useCallback } from "react";
import "./index.css";
import { requestGeolocation, startHeightSync, trackEvent, fuelAction } from "./iframe-bridge";

// Compact format: [brand, addr, muni, lat, lon, p95, diesel, lpg]
type Row = [string, string, string, number | null, number | null, number | null, number | null, number | null];
type Fuel = "p95" | "diesel" | "lpg";
type FuelAvg = { p95: number | null; diesel: number | null; lpg: number | null };

const FUEL_IDX: Record<Fuel, number> = { p95: 5, diesel: 6, lpg: 7 };
const FUEL_LABELS: Record<Fuel, string> = { p95: "95 benzinas", diesel: "Dyzelinas", lpg: "Dujos" };
const DEFAULT_CITY = "Vilniaus m. sav.";

const fmt = (n: number) => n.toFixed(3).replace(".", ",");
const addr = (r: Row) => r[1].includes(",") ? r[1] : `${r[2]}, ${r[1]}`;

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function findNearestMuni(lat: number, lon: number, rows: Row[]): string {
  let bestMuni = DEFAULT_CITY;
  let bestDist = Infinity;
  for (const r of rows) {
    if (r[3] == null || r[4] == null) continue;
    const d = haversineKm(lat, lon, r[3], r[4]);
    if (d < bestDist) {
      bestDist = d;
      bestMuni = r[2];
    }
  }
  return bestMuni;
}

const cityShort = (muni: string) => muni.replace(/ m\. sav\.$/, "").replace(/ r\. sav\.$/, " r.").replace(/ sav\.$/, "");

export function WidgetMobile() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [reportDate, setReportDate] = useState("");
  const [fuel, setFuel] = useState<Fuel>("p95");
  const [city, setCity] = useState(DEFAULT_CITY);
  const [prevAvg, setPrevAvg] = useState<FuelAvg | null>(null);
  const [prevMuniAvg, setPrevMuniAvg] = useState<Record<string, FuelAvg> | null>(null);
  const [locLoading, setLocLoading] = useState(false);
  const [locDetected, setLocDetected] = useState(false);

  useEffect(() => {
    fetch("/data/widget.json")
      .then(r => r.json())
      .then((d: { s: Row[]; d: string; pa: FuelAvg | null; pma: Record<string, FuelAvg> | null }) => {
        setRows(d.s);
        setReportDate(d.d);
        setPrevAvg(d.pa);
        setPrevMuniAvg(d.pma);
      });
  }, []);

  useEffect(() => startHeightSync(), []);

  const handleLocate = useCallback(() => {
    if (!rows) return;
    trackEvent("widget", "filter_location");
    setLocLoading(true);
    requestGeolocation(
      (coords) => {
        const nearest = findNearestMuni(coords.latitude, coords.longitude, rows);
        setCity(nearest);
        setLocDetected(true);
        setLocLoading(false);
      },
      () => { setLocLoading(false); },
    );
  }, [rows]);

  const resetCity = useCallback(() => {
    setCity(DEFAULT_CITY);
    setLocDetected(false);
  }, []);

  const fi = FUEL_IDX[fuel];

  const allWithFuel = useMemo(() => {
    if (!rows) return [];
    return rows.filter(r => r[fi] != null);
  }, [rows, fi]);

  const natAvg = useMemo(() => {
    if (allWithFuel.length === 0) return null;
    return allWithFuel.reduce((t, r) => t + (r[fi] as number), 0) / allWithFuel.length;
  }, [allWithFuel, fi]);

  const cityStations = useMemo(() => {
    return allWithFuel
      .filter(r => r[2] === city)
      .sort((a, b) => (a[fi] as number) - (b[fi] as number));
  }, [allWithFuel, fi, city]);

  const cityAvg = useMemo(() => {
    if (cityStations.length === 0) return null;
    return cityStations.reduce((t, r) => t + (r[fi] as number), 0) / cityStations.length;
  }, [cityStations, fi]);

  const delta = useMemo(() => {
    if (cityAvg == null) return null;
    const prev = prevMuniAvg?.[city]?.[fuel] ?? null;
    if (prev == null) return null;
    return cityAvg - prev;
  }, [cityAvg, prevMuniAvg, fuel, city]);

  const cheapestInCity = useMemo(() => cityStations.slice(0, 5), [cityStations]);

  if (!rows) {
    return (
      <div className="w-full bg-black flex items-center justify-center text-grey-60 font-body py-12">
        Kraunama...
      </div>
    );
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("lt-LT", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "Europe/Vilnius" });
  };

  return (
    <div className="font-body w-full bg-black text-[#e5e5e5] flex flex-col">
      {/* Header */}
      <div className="px-[clamp(8px,3vw,16px)] pt-[clamp(8px,3vw,16px)] pb-1 flex items-center justify-between gap-2">
        <h1 className="font-display font-bold text-[clamp(16px,5.5vw,26px)] leading-tight m-0 text-white">Degalų kainos Lietuvoje</h1>
        {locDetected ? (
          <button
            onClick={resetCity}
            className="py-1 px-3 border border-grey-40 bg-transparent text-white text-[clamp(9px,2.8vw,12px)] font-semibold cursor-pointer hover:border-white transition-all duration-150 whitespace-nowrap"
          >
            ✕ {cityShort(city)}
          </button>
        ) : (
          <button
            onClick={handleLocate}
            disabled={locLoading}
            className="py-1 px-3 border-2 border-white bg-white text-black text-[clamp(9px,2.8vw,12px)] font-semibold cursor-pointer hover:bg-grey-80 hover:text-white transition-all duration-150 disabled:opacity-60 whitespace-nowrap"
          >
            {locLoading ? "..." : "📍 Šalia manęs"}
          </button>
        )}
      </div>

      {/* Fuel tabs */}
      <div className="grid grid-cols-3 px-[clamp(8px,3vw,16px)] py-2">
        {(["p95", "diesel", "lpg"] as Fuel[]).map(f => (
          <button
            key={f}
            onClick={() => { trackEvent("widget", fuelAction(f)); setFuel(f); }}
            className={`py-[clamp(4px,1.5vw,8px)] border text-[clamp(9px,2.8vw,13px)] font-semibold cursor-pointer transition-all duration-150
              ${fuel === f
                ? "bg-white text-black border-white"
                : "bg-transparent text-white border-grey-40 hover:border-white"}`}
          >
            {FUEL_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-[clamp(8px,3vw,16px)] flex flex-col gap-[clamp(4px,1.5vw,8px)]">
        {/* Average price card */}
        <div className="bg-[#047FFE] p-[clamp(8px,3vw,16px)] text-white">
          <div className="text-[clamp(8px,2.5vw,11px)] uppercase tracking-wider font-semibold mb-1 opacity-80">
            {FUEL_LABELS[fuel]} · {cityShort(city)} sav.
          </div>
          <div className="flex items-end justify-between gap-2">
            <div className="font-display font-bold text-[clamp(28px,12vw,56px)] leading-[0.9] tracking-tight">
              {cityAvg != null ? fmt(cityAvg) : "—"}
              <span className="text-[0.35em] font-bold opacity-70"> €/l vidurkis</span>
            </div>
            {delta != null && Math.abs(delta) >= 0.0005 && (
              <span className={`font-body font-bold text-[clamp(10px,2.5vw,13px)] py-1 px-2.5 rounded-full whitespace-nowrap inline-flex items-center gap-1
                ${delta > 0
                  ? "bg-[#ffe539] text-black"
                  : "bg-white/20 text-white border border-white/40"}`}
              >
                {delta > 0 ? "↑" : "↓"} {fmt(Math.abs(delta))} €
              </span>
            )}
          </div>
          {natAvg != null && (
            <div className="mt-0.5 text-[clamp(9px,2.5vw,12px)] opacity-70">
              Lietuvoje vidurkis: <b className="font-semibold opacity-100">{fmt(natAvg)} €/l</b>
            </div>
          )}
        </div>

        {/* Cheapest stations */}
        <div className="text-[clamp(8px,2.5vw,11px)] uppercase tracking-wider font-semibold text-grey-60 mt-1">Mažiausia kaina</div>
        {cheapestInCity.map((r) => (
          <div key={`${r[0]}|${r[1]}`} className="bg-[#1a1a1a] px-[clamp(8px,2.5vw,16px)] py-[clamp(6px,2vw,10px)] flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[clamp(11px,3.2vw,15px)] font-semibold text-white truncate">{r[0]}</div>
              <div className="text-[clamp(9px,2.8vw,13px)] text-grey-60 truncate">{addr(r)}</div>
            </div>
            <div className="font-display font-bold text-[clamp(16px,5.5vw,26px)] text-white leading-none whitespace-nowrap">
              {fmt(r[fi]!)}
              <span className="text-[clamp(7px,2vw,10px)] text-grey-60 ml-0.5">€/l</span>
            </div>
          </div>
        ))}

        {/* Link */}
        <a
          href="https://www.15min.lt/projektas/degalu-kainos"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("widget", "more")}
          className="bg-[#047FFE] text-white text-[clamp(11px,3.5vw,16px)] font-semibold py-[clamp(6px,2vw,12px)] no-underline text-center hover:bg-[#0366d6] transition-colors"
        >
          Daugiau {"\u2192"}
        </a>
      </div>

      {/* Footer */}
      <div className="px-[clamp(8px,3vw,16px)] py-[clamp(6px,2vw,10px)]">
        <span className="text-[clamp(7px,2.2vw,10px)] text-grey-60 leading-tight block">Duomenys atnaujinti: {formatDate(reportDate)} 10:00 · Atnaujinama kasdien · Šaltinis: LEA</span>
      </div>
    </div>
  );
}

export default WidgetMobile;
